'use client';

import React from 'react';
import cn from 'classnames';
import { LogoResearchRoomHorizontal } from '@dealroadshow/uikit-logo/ResearchRoom/Horizontal';
import { usePathname } from '@framework/next/navigation';
import Header from '@framework/ui/Pages/Landing/Sections/Header';
import CookiesBanner from '@framework/cookie-consent/CookiesBanner';
import useLoginUrlWithConsent from '@framework/cookie-consent/useLoginUrlWithConsent';
import ScrollBodyBackground from '@framework/ui/Templates/ScrollBodyBackground';
import Footer from './components/Footer';
import DataroomTenantContext from '@dataroom/tenant/DataroomTenantContext';
import config from '@/dataroom/app/config/config';
import stickyFooterStyles from '@framework/ui/Organisms/StickyFooter/stickyFooter.module.scss';
import styles from './tenantResearchRoomStyles.module.scss';
import BookDemoButton from '@/dataroom/ui/common/BookDemoButton';
import dataroomUrl from '@/dataroom/infrastructure/dataroomUrl';
import dmPortalUrl from '@dm-portal/url';
import { TabLink as NextNavLink } from '@framework/router/Next/Link';
import Button from '@dealroadshow/uikit-button';

interface IProps {
  children: React.ReactNode,
}

const getHeaderMenu = (pathname: string) => [
  {
    key: 'features',
    title: 'Features',
    path: '/features',
  },
  {
    key: 'process',
    title: 'Process',
    path: '/process',
  },
  {
    key: 'regulations',
    title: 'Regulations',
    path: '/regulations',
    isActive: () => pathname.startsWith('/regulations'),
  },
  {
    key: 'pricing',
    title: 'Pricing',
    path: '/pricing',
    isActive: () => pathname.startsWith('/pricing'),
  },
];

const TenantResearchRoom = ({ children }: IProps) => {
  const tenant = config.tenant.tenantResearchRoom.code;
  const pathname = usePathname();
  const loginUrl = useLoginUrlWithConsent(() =>
    dataroomUrl(tenant).getLoginUrl(dmPortalUrl.getUrl(`/${ tenant }`)),
  );

  return (
    <DataroomTenantContext tenant={ tenant }>
      <Header
        logoComponent={ LogoResearchRoomHorizontal }
        logoClassName={ styles.largeLogo }
        menu={ getHeaderMenu(pathname) }
        isWideMenu
        headerButton={ ({ buttonProps }) => (
          <BookDemoButton
            { ...buttonProps }
            className={ styles.bookDemoButton }
          />
        ) }
        mobileMenuClassName={ styles.mobileMenu }
        customLoginButton={ ({
          buttonProps,
          linkProps,
        }) => (
          <NextNavLink
            { ...linkProps }
            to={ loginUrl }
          >
            <Button { ...buttonProps } />
          </NextNavLink>
        ) }
      />
      <div className={ cn(styles.pageContainer, stickyFooterStyles.pageContainerWithStickyFooter) }>
        <ScrollBodyBackground tenant={ tenant } minHeight={ 1000 } />
        { children }
      </div>
      <Footer tenant={ tenant } />
      <CookiesBanner />
    </DataroomTenantContext>
  );
};

export default TenantResearchRoom;
